import { BottomSheet, Button, SideSheet, Text } from '@/components/DesignSystem';
import { DesktopOnly } from '@/components/shared/DesktopOnly';
import { MobileOnly } from '@/components/shared/MobileOnly';
import { OrderTransactionEndpoint } from '@api/endpoints/OrderTransactionEndpoint';
import { Box, Divider, Stack, TextField, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PointsRangeFilter } from './types';

interface PointsRangeFilterModalProps {
  onApply: (filter: PointsRangeFilter) => void;
  onClose: () => void;
  open: boolean;
  themeColor?: string;
  value?: PointsRangeFilter;
}

type RangeState = {
  remaining: { min: number | null; max: number | null };
  redeemed: { min: number | null; max: number | null };
  reserved: { min: number | null; max: number | null };
};

const INITIAL_STATE: RangeState = {
  remaining: { min: null, max: null },
  redeemed: { min: null, max: null },
  reserved: { min: null, max: null },
};

const RANGE_CONFIG = [
  { key: 'remaining' as const },
  { key: 'redeemed' as const },
  { key: 'reserved' as const },
] as const;

export const PointsRangeFilterModal: React.FC<PointsRangeFilterModalProps> = ({
  onApply,
  onClose,
  open,
  themeColor,
  value,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [ranges, setRanges] = useState<RangeState>(INITIAL_STATE);
  const [error, setError] = useState<keyof RangeState | null>(null);

  const rangeLabels: Record<keyof RangeState, string> = {
    remaining: t(($) => $.bookings.filters.pointsRemaining),
    redeemed: t(($) => $.bookings.filters.pointsRedeemed),
    reserved: t(($) => $.bookings.filters.pointsReserved),
  };

  const setValuesFromProp = useCallback((filterValue?: PointsRangeFilter) => {
    setRanges({
      remaining: {
        min: filterValue?.remaining?.min ?? null,
        max: filterValue?.remaining?.max ?? null,
      },
      redeemed: {
        min: filterValue?.redeemed?.min ?? null,
        max: filterValue?.redeemed?.max ?? null,
      },
      reserved: {
        min: filterValue?.reserved?.min ?? null,
        max: filterValue?.reserved?.max ?? null,
      },
    });
    setError(null);
  }, []);

  useEffect(() => {
    if (open) {
      setValuesFromProp(value);
    }
  }, [open, value, setValuesFromProp]);

  const handleClose = useCallback(() => {
    onClose();
    setError(null);
  }, [onClose]);

  const handleClear = useCallback(() => {
    setRanges(INITIAL_STATE);
    setError(null);
  }, []);

  const validateRanges = (): boolean => {
    for (const { key } of RANGE_CONFIG) {
      const { min, max } = ranges[key];
      if (min !== null && max !== null && min >= max) {
        setError(key);
        return false;
      }
    }
    return true;
  };

  const buildRange = (min: number | null, max: number | null):
    OrderTransactionEndpoint.PointsRange | undefined => {
    if (min === null && max === null) return undefined;
    return { ...(min !== null && { min }), ...(max !== null && { max }) };
  };

  const handleApply = useCallback(() => {
    if (!validateRanges()) return;

    const filter: PointsRangeFilter = {};
    for (const { key } of RANGE_CONFIG) {
      const range = buildRange(ranges[key].min, ranges[key].max);
      if (range) {
        filter[key] = range;
      }
    }

    onApply(filter);
    handleClose();
  }, [ranges, onApply, handleClose]);

  const updateRange = useCallback((
    key: keyof RangeState,
    field: 'min' | 'max',
    value: number | null,
  ) => {
    setRanges((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  }, []);

  const createChangeHandler = useCallback((
    key: keyof RangeState,
    field: 'min' | 'max',
    compareValue: number | null,
  ) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      if (inputValue === '') {
        updateRange(key, field, null);
        setError(null);
      } else {
        const numValue = parseInt(inputValue, 10);
        if (!isNaN(numValue) && numValue >= 0) {
          updateRange(key, field, numValue);
          const isMin = field === 'min';
          const hasError = isMin
            ? compareValue !== null && numValue >= compareValue
            : compareValue !== null && numValue <= compareValue;
          setError(hasError ? key : null);
        }
      }
    };
  }, [updateRange]);

  const textFieldSx = {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: themeColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: themeColor,
      },
      '& .MuiInputAdornment-root': {
        display: 'none !important',
      },
    },
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '& input::-webkit-search-cancel-button': {
      display: 'none !important',
    },
    '& input::-ms-clear': {
      display: 'none !important',
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-input': {
      paddingRight: '16px !important',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: themeColor,
    },
  };

  const applyButtonSx = {
    backgroundColor: themeColor,
    '&:hover': {
      backgroundColor: themeColor,
      opacity: 0.9,
    },
  };

  const clearButtonSx = {
    borderColor: themeColor,
    color: themeColor,
    '&:hover': {
      borderColor: themeColor,
      backgroundColor: 'transparent',
      color: themeColor,
    },
  };

  const MobileFooter = () => (
    <Box
      alignItems="center"
      display="flex"
      gap={2}
      justifyContent="space-between"
      minHeight={79}
      px={2}
    >
      <Button
        fullWidth
        onClick={handleClear}
        sx={clearButtonSx}
        testID="points-range-filter-clear-button"
        variant="outlined"
      >
        <Text category="label-large" sx={{ fontWeight: 500, color: themeColor }}>
          {t($ => $.common.actions.clearAll)}
        </Text>
      </Button>
      <Button
        fullWidth
        onClick={() => {
          handleApply();
        }}
        sx={applyButtonSx}
        testID="points-range-filter-apply-button"
        variant="contained"
      >
        <Text category="label-large" sx={{ color: theme.palette.common.white }}>
          {t($ => $.common.actions.apply)}
        </Text>
      </Button>
    </Box>
  );

  const renderRangeInputs = (key: keyof RangeState) => {
    const { min, max } = ranges[key];
    const label = rangeLabels[key];
    const hasError = error === key;
    const errorMessage = hasError
      ? t(($) => $.bookings.filters.rangeValidationError, { label: label.toLowerCase() })
      : undefined;

    return (
      <Stack spacing={2}>
        <Text category="title-medium">{label}</Text>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <TextField
            InputProps={{
              endAdornment: null,
              disableUnderline: true,
            }}
            error={hasError}
            helperText={errorMessage}
            inputProps={{ style: { textAlign: 'left' } }}
            label={t($ => $.common.filter.min)}
            onChange={createChangeHandler(key, 'min', max)}
            placeholder={t($ => $.common.filter.min)}
            sx={textFieldSx}
            type="number"
            value={min === null ? '' : min}
          />
          <TextField
            InputProps={{
              endAdornment: null,
              disableUnderline: true,
            }}
            error={hasError}
            helperText={errorMessage}
            inputProps={{ style: { textAlign: 'left' } }}
            label={t($ => $.common.filter.max)}
            onChange={createChangeHandler(key, 'max', min)}
            placeholder={t($ => $.common.filter.max)}
            sx={textFieldSx}
            type="number"
            value={max === null ? '' : max}
          />
        </Stack>
      </Stack>
    );
  };

  const renderContent = () => (
    <Stack px="24px" py="16px" spacing={3}>
      {RANGE_CONFIG.map(({ key }, index) => (
        <React.Fragment key={key}>
          {index > 0 && <Divider />}
          {renderRangeInputs(key)}
        </React.Fragment>
      ))}
    </Stack>
  );

  return (
    <>
      <DesktopOnly>
        <SideSheet
          actionButton={(props) => (
            <Button
              {...props}
              onClick={handleApply}
              sx={applyButtonSx}
              testID="points-range-filter-apply-button"
              variant="contained"
            >
              {t($ => $.common.actions.apply)}
            </Button>
          )}
          hideScrollbar
          onClose={handleClose}
          onOpen={() => setValuesFromProp(value)}
          open={open}
          secondaryActionButton={(props) => (
            <Button
              {...props}
              onClick={handleClear}
              sx={clearButtonSx}
              testID="points-range-filter-clear-button"
              variant="outlined"
            >
              <Text sx={{ fontWeight: 500, color: themeColor }}>
                {t($ => $.common.actions.clearAll)}
              </Text>
            </Button>
          )}
          testID="points-range-filter-sheet"
          title={t($ => $.bookings.filters.rangeTitle)}
        >
          {renderContent()}
        </SideSheet>
      </DesktopOnly>
      <MobileOnly>
        <BottomSheet
          footer={<MobileFooter />}
          onClose={handleClose}
          onOpen={() => setValuesFromProp(value)}
          open={open}
          showCloseButton={true}
          showPuller={true}
          testID="points-range-filter-sheet"
          title={t($ => $.bookings.filters.rangeTitle)}
        >
          {renderContent()}
        </BottomSheet>
      </MobileOnly>
    </>
  );
};
